import { useCallback, useSyncExternalStore } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  getToken,
  setToken as persistToken,
  subscribeTokenChange,
  wipeSession,
} from "@/lib/auth/session";
import { clearPersistedCache } from "@/lib/persist/query-persister";

/**
 * Auth hook. The token is sourced from a module-scope store via
 * useSyncExternalStore so every subscriber re-renders when setToken is
 * called — even components mounted before the token existed (e.g. AppShell
 * at the router root).
 */
export function useAuth(): {
  token: string | null;
  isAuthenticated: boolean;
  setToken: (pat: string) => void;
  signOut: () => Promise<void>;
} {
  const token = useSyncExternalStore(subscribeTokenChange, getToken, getToken);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const setToken = useCallback((pat: string) => {
    persistToken(pat);
    // useSyncExternalStore picks up the change via the notify() in persistToken.
  }, []);

  const signOut = useCallback(async () => {
    // Sync wipes first: sessionStorage + in-memory react-query cache.
    wipeSession();
    queryClient.clear();
    // Await the async IndexedDB wipe BEFORE navigating so a second user on a
    // shared browser can never hydrate the previous user's cache on mount.
    try {
      await clearPersistedCache();
    } catch {
      // clearPersistedCache itself already swallows errors; this is belt-and-braces.
    }
    navigate("/", { replace: true });
  }, [navigate, queryClient]);

  return {
    token,
    isAuthenticated: token != null,
    setToken,
    signOut,
  };
}
